import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    Alert,
    CircularProgress,
    Grid,
    Box
} from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { updateProfile } from '../../Utils/accountApi';
import { useTranslation } from '../../hooks/useTranslation';

export default function ProfileTab() {
    const { currentUser, canEditOwnProfile, loading: permissionsLoading, refreshUserData } = usePermissions();
    const t = useTranslation('Profile.json');
    const tCommon = useTranslation('Common.json');

    const [formData, setFormData] = useState({
        nom: '',
        prenom: '',
        email: '',
        phone: ''
    });

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (currentUser) {
            setFormData({
                nom: currentUser.nom || '',
                prenom: currentUser.prenom || '',
                email: currentUser.email || '',
                phone: currentUser.phone || ''
            });
        }
    }, [currentUser]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setSuccess(false);
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            await updateProfile(formData);
            await refreshUserData();
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error('Erreur lors de la mise à jour du profil:', err);
            setError(err.response?.data?.error || t('profile.error.generic'));
        } finally {
            setLoading(false);
        }
    };

    if (permissionsLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress sx={{ color: '#FF7233' }} />
            </Box>
        );
    }

    if (!currentUser) {
        return (
            <Alert severity="error">
                {t('profile.no_user')}
            </Alert>
        );
    }

    return (
        <Box>
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {t('profile.success')}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('profile.field.last_name')}
                            name="nom"
                            value={formData.nom}
                            onChange={handleChange}
                            disabled={!canEditOwnProfile || loading}
                            required
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('profile.field.first_name')}
                            name="prenom"
                            value={formData.prenom}
                            onChange={handleChange}
                            disabled={!canEditOwnProfile || loading}
                            required
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('profile.field.email')}
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={!canEditOwnProfile || loading}
                            required
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('profile.field.phone')}
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            disabled={!canEditOwnProfile || loading}
                            placeholder="+33612345678"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            {canEditOwnProfile && (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    sx={{
                                        bgcolor: '#FF7233',
                                        '&:hover': {
                                            bgcolor: '#e05a1a'
                                        }
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} /> : tCommon('common.save')}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </form>

            {!canEditOwnProfile && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {t('profile.no_permission')}
                </Alert>
            )}
        </Box>
    );
}
